import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { MainLayout } from "./components/layout/MainLayout";
import { ChampionshipLayout } from "./components/layout/ChampionshipLayout";
import { Toaster } from "sonner";
import { useDynamicTitle } from "./hooks/useDynamicTitle";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import ChampionshipsPage from "./pages/ChampionshipsPage";
import ChampionshipFormPage from "./pages/ChampionshipFormPage";
import ChampionshipDetailPage from "./pages/ChampionshipDetailPage";
import FederationsPage from "./pages/FederationsPage";
import VincularFederacaoPage from "./pages/VincularFederacaoPage";
import DocumentosPage from "./pages/DocumentosPage";
import TeamsPage from "./pages/TeamsPage";
import AthletesPage from "./pages/AthletesPage";
import AthleteCardPage from "./pages/AthleteCardPage";
import AthleteFichaPage from "./pages/AthleteFichaPage";
import MinhaEquipePage from "./pages/MinhaEquipePage";
import WeightCategoriesPage from "./pages/WeightCategoriesPage";
import RegistrationsPage from "./pages/RegistrationsPage";
import CentralDeskPage from "./pages/CentralDeskPage";
import ScoreboardPage from "./pages/ScoreboardPage";
import ScoreboardDisplay from "./pages/ScoreboardDisplay";
import MedalsPage from "./pages/MedalsPage";
import ChampionshipRankingPage from "./pages/ChampionshipRankingPage";
import FederativoRankingPage from "./pages/FederativoRankingPage";
import FederativoRulesPage from "./pages/FederativoRulesPage";
import EventStructurePage from "./pages/EventStructurePage";
import MyProfilePage from "./pages/MyProfilePage";
import AuditPage from "./pages/AuditPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import FightTimesPage from "./pages/FightTimesPage";
import PublicPage, { PublicResultsPage } from "./pages/PublicPage";
import PublicAthletesPage from "./pages/PublicAthletesPage";
import PublicRankingPage from "./pages/PublicRankingPage";
import TelaoPage from "./pages/TelaoPage";
import AdminResetPage from "./pages/AdminResetPage";
import UsersAdminPage from "./pages/UsersAdminPage";
import BackupRestorePage from "./pages/BackupRestorePage";
import PublicRankingConsolidatedPage from "./pages/PublicRankingConsolidatedPage";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  useDynamicTitle();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/public" element={<PublicPage />} />
      <Route path="/public/:id" element={<PublicResultsPage />} />
      <Route path="/public/:id/athletes" element={<PublicAthletesPage />} />
      <Route path="/public/:id/ranking" element={<PublicRankingPage />} />
      <Route path="/public/:id/telao" element={<TelaoPage />} />
      <Route path="/public/ranking" element={<PublicRankingConsolidatedPage />} />

      {/* ====================================================================
          LAYOUT DE ROTA — O SHELL MONTA UMA VEZ SÓ (correção da sidebar)

          Antes, cada uma das 17 páginas montava o seu próprio MainLayout.
          Navegar entre páginas desmontava e remontava a Sidebar, e o menu
          voltava ao topo a cada clique (o `<nav>` novo nasce com scrollTop 0).

          Agora o shell vive em volta das rotas internas, então ele NUNCA é
          recriado: a rolagem do menu, o menu mobile e o foco do teclado são
          preservados entre navegações.

          As rotas internas mantêm o ProtectedRoute delas (que checa as `roles`);
          a checagem de sessão aqui é redundante de propósito, para não precisar
          mexer em 40 elementos de rota.

          FORA daqui ficam as telas de EXIBIÇÃO EM TELA CHEIA (Placar/Telão),
          que de propósito NÃO têm shell — ver o comentário mais abaixo.
          ==================================================================== */}
      <Route element={<ProtectedRoute><MainLayout><ErrorBoundary><Outlet /></ErrorBoundary></MainLayout></ProtectedRoute>}>
      {/* Protected Routes — Global */}
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      
      <Route path="/championships" element={
        <ProtectedRoute><ChampionshipsPage /></ProtectedRoute>
      } />
      <Route path="/championships/new" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><ChampionshipFormPage /></ProtectedRoute>
      } />
      <Route path="/championships/:id/edit" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><ChampionshipFormPage /></ProtectedRoute>
      } />

      {/* Championship nested routes — single shared Sidebar layout */}
      <Route path="/championships/:id" element={
        <ProtectedRoute><ChampionshipLayout /></ProtectedRoute>
      }>
        <Route index element={<ChampionshipDetailPage />} />
        <Route path="registrations" element={<RegistrationsPage />} />
        <Route path="fight-times" element={<FightTimesPage />} />
        <Route path="event-structure" element={<EventStructurePage />} />
        <Route path="central-desk" element={<CentralDeskPage />} />
        <Route path="placar" element={<ScoreboardPage />} />
        {/* ATENÇÃO: /championships/:id/placar/exibir NÃO fica aqui. O placar em
            modo TV é `h-screen w-screen overflow-hidden`; dentro deste layout
            (sidebar + `p-8` + `max-w-7xl`) a altura somava e a largura era
            cortada, deixando cronômetro/botões fora da tela. A mesma URL é
            declarada FORA do layout aninhado, junto das operacionais. */}
        <Route path="medals" element={<MedalsPage />} />
        {/* Ranking DO CAMPEONATO (:id) — lê live-ranking/team-ranking do
            próprio campeonato. */}
        <Route path="ranking" element={<ChampionshipRankingPage />} />
      </Route>

      {/* Global operational routes — backward compatibility */}
      <Route path="/registrations" element={
        <ProtectedRoute><RegistrationsPage /></ProtectedRoute>
      } />
      <Route path="/event-structure" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><EventStructurePage /></ProtectedRoute>
      } />
      <Route path="/central-desk" element={
        <ProtectedRoute roles={['super_admin', 'federation', 'staff', 'central_desk']}><CentralDeskPage /></ProtectedRoute>
      } />
      <Route path="/placar" element={
        <ProtectedRoute roles={['super_admin', 'federation', 'staff', 'central_desk']}><ScoreboardPage /></ProtectedRoute>
      } />
      </Route>

      {/* ---------- SEM SHELL: exibição em tela cheia (Placar/Telão) ----------
          Estas duas rotas renderizam o placar em tela cheia de propósito, sem
          sidebar. Ficam FORA do layout de rota para não ganharem o shell. */}
      <Route path="/placar/exibir" element={
        <ProtectedRoute roles={['super_admin', 'federation', 'staff', 'central_desk']}><ScoreboardDisplay /></ProtectedRoute>
      } />
      {/* Placar em modo TV no contexto do campeonato: MESMA tela cheia, sem
          MainLayout (o placar lê o campeonato por `?championship=`). A URL é
          exatamente a mesma de antes — muda só quem renderiza (sem o shell). */}
      <Route path="/championships/:id/placar/exibir" element={
        <ProtectedRoute roles={['super_admin', 'federation', 'staff', 'central_desk']}><ScoreboardDisplay /></ProtectedRoute>
      } />
      {/* Volta o shell para as rotas internas comuns */}
      <Route element={<ProtectedRoute><MainLayout><ErrorBoundary><Outlet /></ErrorBoundary></MainLayout></ProtectedRoute>}>
      <Route path="/medals" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><MedalsPage /></ProtectedRoute>
      } />
      <Route path="/ranking-federativo" element={
        <ProtectedRoute><FederativoRankingPage /></ProtectedRoute>
      } />
      <Route path="/ranking-federativo/regras" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><FederativoRulesPage /></ProtectedRoute>
      } />
      <Route path="/fight-times" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><FightTimesPage /></ProtectedRoute>
      } />

      {/* Other Global Routes */}
      <Route path="/federations" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><FederationsPage /></ProtectedRoute>
      } />
      <Route path="/federacoes/vincular" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><VincularFederacaoPage /></ProtectedRoute>
      } />
      <Route path="/documentos" element={
        <ProtectedRoute><DocumentosPage /></ProtectedRoute>
      } />
      <Route path="/documentos/fila" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><DocumentosPage modo="fila" /></ProtectedRoute>
      } />
      <Route path="/teams" element={
        <ProtectedRoute roles={['super_admin', 'federation', 'professor']}><TeamsPage /></ProtectedRoute>
      } />
      <Route path="/athletes" element={
        <ProtectedRoute><AthletesPage /></ProtectedRoute>
      } />
      <Route path="/athletes/new" element={
        <ProtectedRoute><AthletesPage /></ProtectedRoute>
      } />
      <Route path="/athletes/:id/card" element={
        <ProtectedRoute><AthleteCardPage /></ProtectedRoute>
      } />
      <Route path="/athletes/:id/ficha" element={
        <ProtectedRoute><AthleteFichaPage /></ProtectedRoute>
      } />
      {/* Mini gerenciamento de alunos (turmas, chamada e mensalidades):
          super_admin, professor, equipe e o MONITOR ATIVO (aluno com vínculo
          na equipe — `permitirMonitor`; o papel dele continua `athlete`). */}
      <Route path="/minha-equipe" element={
        <ProtectedRoute roles={['super_admin', 'professor', 'team']} permitirMonitor><MinhaEquipePage /></ProtectedRoute>
      } />
      <Route path="/weight-categories" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><WeightCategoriesPage /></ProtectedRoute>
      } />
      <Route path="/my-profile" element={
        <ProtectedRoute roles={['athlete', 'professor']}><MyProfilePage /></ProtectedRoute>
      } />
      <Route path="/audit" element={
        <ProtectedRoute roles={['super_admin', 'federation']}><AuditPage /></ProtectedRoute>
      } />
      <Route path="/admin/reset" element={
        <ProtectedRoute roles={['super_admin']}><AdminResetPage /></ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute roles={['super_admin']}><UsersAdminPage /></ProtectedRoute>
      } />
      <Route path="/admin/backup" element={
        <ProtectedRoute roles={['super_admin']}><BackupRestorePage /></ProtectedRoute>
      } />
      </Route>

      {/* ETAPA 11 — a página pública virou a PÁGINA INICIAL: quem chega sem
          sessão cai aqui, onde já existe o acesso (login/cadastro embutidos no
          hero). `/public` continua valendo, porque há links antigos para ele. */}
      <Route path="/" element={<PublicPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary><AppRouter /></ErrorBoundary>
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            className: 'theme-aware-toast',
            style: {
              background: '#141414',
              border: '1px solid #27272A',
              color: '#fff',
            },
          }}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
